// SafetyCrossView — reusable monthly + annual safety cross with configurable
// message, per-client settings (FTE/hours/target/message) and click-to-view.
import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Loader2, ShieldAlert, Plus, Settings, Save, CalendarDays, LayoutGrid } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useView } from "@/context/ViewContext";
import { IncidentDialog } from "@/pages/safety/IncidentsPage";

const PORTAL_VIEWER_ROLES = new Set(["reviewer"]);
const INTERNAL_ROLES = new Set(["director", "administrator", "consultant"]);

const COLOUR_TO_BG = { green: "#3F7D3A", yellow: "#E6C04F", orange: "#D87A1F", red: "#B43A3A", grey: "#E5E0DA" };
const COLOUR_TO_FG = { green: "#FFFFFF", yellow: "#1F1F1F", orange: "#FFFFFF", red: "#FFFFFF", grey: "#9A968F" };
const COLOUR_LABEL = { green: "No incident", yellow: "Minor / first-aid", orange: "Moderate / recordable", red: "Major / RIDDOR", grey: "No data" };
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const inputCls = "w-full h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm focus:outline-none focus:border-[var(--brand-accent,_#C75A1B)]";

export default function SafetyCrossView({ initialClientId, initialBuildingId }) {
    const today = new Date();
    const { user } = useAuth();
    const { portalClientId, isInternalView } = useView();
    const viewerCanEdit = !PORTAL_VIEWER_ROLES.has(user?.role || "");
    const canEditSettings = INTERNAL_ROLES.has(user?.role || "");

    const [clients, setClients] = useState([]);
    const [buildings, setBuildings] = useState([]);
    const [clientId, setClientId] = useState(initialClientId || "");
    const [buildingId, setBuildingId] = useState(initialBuildingId || "");
    const [year, setYear] = useState(today.getFullYear());
    const [month, setMonth] = useState(today.getMonth() + 1);
    const [mode, setMode] = useState("month");      // month | annual
    const [data, setData] = useState(null);
    const [annual, setAnnual] = useState(null);
    const [loading, setLoading] = useState(false);
    const [selectedDay, setSelectedDay] = useState(null);
    const [meta, setMeta] = useState({ types: [], severities: [], statuses: [] });
    const [openIncident, setOpenIncident] = useState(null);
    const [newIncident, setNewIncident] = useState(null);
    const [dayModal, setDayModal] = useState(null);  // {date, incidents}
    const [showSettings, setShowSettings] = useState(false);
    const [config, setConfig] = useState(null);

    useEffect(() => {
        api.get("/incidents/meta").then((r) => setMeta(r.data)).catch(() => { });
        api.get("/clients").then((r) => {
            setClients(r.data);
            if (!clientId) {
                if (portalClientId && !isInternalView) setClientId(portalClientId);
                else if (r.data?.[0]) setClientId(r.data[0].id);
            }
        }).catch(() => { });
        api.get("/buildings").then((r) => setBuildings(r.data)).catch(() => { });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!clientId) return;
        api.get(`/incidents/safety-config/${clientId}`).then((r) => setConfig(r.data)).catch(() => setConfig(null));
    }, [clientId]);

    const refresh = useCallback(async () => {
        if (!clientId) return;
        setLoading(true);
        try {
            if (mode === "month") {
                const params = { year, month };
                if (buildingId) params.building_id = buildingId;
                const { data: body } = await api.get(`/safety-cross/${clientId}`, { params });
                setData(body);
            } else {
                const params = { year };
                if (buildingId) params.building_id = buildingId;
                const { data: body } = await api.get(`/safety-cross/${clientId}/annual`, { params });
                setAnnual(body);
            }
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not load safety cross");
        }
        setLoading(false);
    }, [clientId, buildingId, year, month, mode]);

    useEffect(() => { refresh(); }, [refresh]);

    const filteredBuildings = useMemo(() => buildings.filter((b) => !clientId || b.client_id === clientId), [buildings, clientId]);

    function changeMonth(dir) {
        let m = month + dir, y = year;
        if (m < 1) { m = 12; y -= 1; }
        if (m > 12) { m = 1; y += 1; }
        setMonth(m); setYear(y); setSelectedDay(null);
    }

    async function openIncidentDialog(incidentId) {
        try { const { data } = await api.get(`/incidents/${incidentId}`); setOpenIncident(data); }
        catch (e) { toast.error("Could not load incident"); }
    }

    async function saveConfig() {
        try {
            const { data } = await api.put(`/incidents/safety-config/${clientId}`, {
                annual_fte: config.annual_fte === "" ? null : Number(config.annual_fte),
                annual_hours_worked: config.annual_hours_worked === "" ? null : Number(config.annual_hours_worked),
                target_rate: config.target_rate === "" ? null : Number(config.target_rate),
                rate_basis: config.rate_basis,
                safety_cross_message: config.safety_cross_message,
            });
            setConfig(data);
            setShowSettings(false);
            toast.success("Safety settings saved");
            refresh();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    }

    const cells = data?.cells || [];
    const grid = [...Array(data?.first_weekday ?? 0).fill(null), ...cells];
    const selected = cells.find((c) => c.day === selectedDay);
    const message = (mode === "month" ? data?.message : annual?.message) || config?.safety_cross_message;

    return (
        <div data-testid="safety-cross-view" className="space-y-4">
            {/* Configurable message banner */}
            {message && (
                <div data-testid="safety-cross-message" className="rounded-xl px-5 py-3 text-center text-white font-semibold tracking-wide"
                    style={{ background: "var(--brand-accent, #C75A1B)" }}>
                    {message}
                </div>
            )}

            {/* Controls */}
            <div className="flex items-center gap-3 flex-wrap">
                <select data-testid="safety-cross-client" value={clientId}
                    onChange={(e) => { setClientId(e.target.value); setBuildingId(""); setSelectedDay(null); }}
                    disabled={!!(portalClientId && !isInternalView)}
                    className="h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm">
                    <option value="">— Client —</option>
                    {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <select data-testid="safety-cross-building" value={buildingId}
                    onChange={(e) => { setBuildingId(e.target.value); setSelectedDay(null); }}
                    className="h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm">
                    <option value="">All buildings</option>
                    {filteredBuildings.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
                <div className="inline-flex rounded-[9px] border border-[#E5E0DA] overflow-hidden">
                    {[["month", "Monthly", CalendarDays], ["annual", "Annual", LayoutGrid]].map(([id, label, Icon]) => (
                        <button key={id} data-testid={`safety-cross-mode-${id}`} onClick={() => setMode(id)}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[12.5px] font-semibold ${mode === id ? "text-white" : "text-[#6F6A64] hover:bg-[#F6F4F0]"}`}
                            style={mode === id ? { background: "var(--brand-accent, #C75A1B)" } : undefined}>
                            <Icon size={13} /> {label}
                        </button>
                    ))}
                </div>
                <div className="ml-auto flex items-center gap-2">
                    {canEditSettings && (
                        <button data-testid="safety-cross-settings-btn" onClick={() => setShowSettings((s) => !s)}
                            className="h-9 px-3 inline-flex items-center gap-1.5 rounded-lg border border-[#E5E0DA] bg-white text-[12.5px] font-semibold text-[#6F6A64] hover:bg-[#F6F4F0]">
                            <Settings size={14} /> Settings
                        </button>
                    )}
                    {mode === "month" ? (
                        <>
                            <button data-testid="safety-cross-prev" onClick={() => changeMonth(-1)}
                                className="h-9 w-9 grid place-items-center rounded-lg border border-[#E5E0DA] bg-white hover:bg-[#F5F3F0]"><ChevronLeft size={16} /></button>
                            <div className="text-sm font-semibold text-[#1F1F1F] w-40 text-center">{data ? `${data.month_name} ${data.year}` : `${month}/${year}`}</div>
                            <button data-testid="safety-cross-next" onClick={() => changeMonth(1)}
                                className="h-9 w-9 grid place-items-center rounded-lg border border-[#E5E0DA] bg-white hover:bg-[#F5F3F0]"><ChevronRight size={16} /></button>
                        </>
                    ) : (
                        <>
                            <button onClick={() => setYear((y) => y - 1)} className="h-9 w-9 grid place-items-center rounded-lg border border-[#E5E0DA] bg-white hover:bg-[#F5F3F0]"><ChevronLeft size={16} /></button>
                            <div className="text-sm font-semibold text-[#1F1F1F] w-24 text-center">{year}</div>
                            <button onClick={() => setYear((y) => y + 1)} className="h-9 w-9 grid place-items-center rounded-lg border border-[#E5E0DA] bg-white hover:bg-[#F5F3F0]"><ChevronRight size={16} /></button>
                        </>
                    )}
                </div>
            </div>

            {showSettings && config && (
                <div data-testid="safety-cross-settings" className="rounded-xl border border-[#E5E0DA] bg-white p-4 space-y-3">
                    <h4 className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64]">Safety settings — {clients.find((c) => c.id === clientId)?.name}</h4>
                    <div className="grid sm:grid-cols-4 gap-3">
                        <div>
                            <label className="block text-[11px] font-bold uppercase tracking-wider text-[#6F6A64] mb-1">Annual FTE</label>
                            <input data-testid="cfg-fte" type="number" value={config.annual_fte ?? ""} onChange={(e) => setConfig({ ...config, annual_fte: e.target.value })} className={inputCls} />
                        </div>
                        <div>
                            <label className="block text-[11px] font-bold uppercase tracking-wider text-[#6F6A64] mb-1">Annual hours worked</label>
                            <input data-testid="cfg-hours" type="number" value={config.annual_hours_worked ?? ""} onChange={(e) => setConfig({ ...config, annual_hours_worked: e.target.value })} className={inputCls} />
                        </div>
                        <div>
                            <label className="block text-[11px] font-bold uppercase tracking-wider text-[#6F6A64] mb-1">Target rate</label>
                            <input data-testid="cfg-target" type="number" value={config.target_rate ?? ""} onChange={(e) => setConfig({ ...config, target_rate: e.target.value })} className={inputCls} />
                        </div>
                        <div>
                            <label className="block text-[11px] font-bold uppercase tracking-wider text-[#6F6A64] mb-1">Rate basis</label>
                            <select data-testid="cfg-basis" value={config.rate_basis || "AFR"} onChange={(e) => setConfig({ ...config, rate_basis: e.target.value })} className={inputCls}>
                                <option value="AFR">AFR (per 100k hours)</option>
                                <option value="AIR">AIR (per 100k FTE)</option>
                            </select>
                        </div>
                    </div>
                    <div>
                        <label className="block text-[11px] font-bold uppercase tracking-wider text-[#6F6A64] mb-1">Safety cross message</label>
                        <input data-testid="cfg-message" value={config.safety_cross_message ?? ""} onChange={(e) => setConfig({ ...config, safety_cross_message: e.target.value })} className={inputCls} />
                    </div>
                    <button data-testid="cfg-save" onClick={saveConfig}
                        className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-[12.5px] font-semibold text-white" style={{ background: "var(--brand-accent, #C75A1B)" }}>
                        <Save size={13} /> Save settings
                    </button>
                </div>
            )}

            {loading ? (
                <div className="text-center text-[#9A968F] py-10"><Loader2 size={16} className="inline animate-spin mr-2" />Loading…</div>
            ) : mode === "month" ? (
                <div className="grid grid-cols-3 gap-4">
                    <div className="col-span-2 rounded-2xl bg-white border border-[#E5E0DA] p-5">
                        {!data ? <div className="text-center text-[#9A968F] py-10">Select a client.</div> : (<>
                            <div className="grid grid-cols-7 gap-1 mb-1 text-[10px] uppercase tracking-wider text-[#9A968F] font-bold">
                                {WEEKDAYS.map((w) => <div key={w} className="text-center py-1">{w}</div>)}
                            </div>
                            <div className="grid grid-cols-7 gap-1.5">
                                {grid.map((cell, idx) => {
                                    if (!cell) return <div key={`pad-${idx}`} />;
                                    const isSel = selectedDay === cell.day;
                                    return (
                                        <button key={cell.day} data-testid={`safety-cross-day-${cell.day}`} data-colour={cell.colour}
                                            onClick={() => setSelectedDay(cell.day)}
                                            className={`aspect-square rounded-lg flex flex-col items-center justify-center text-sm transition transform ${isSel ? "ring-2 ring-offset-2 ring-[var(--brand-accent,_#C75A1B)] scale-[1.03]" : "hover:scale-[1.02]"}`}
                                            style={{ background: COLOUR_TO_BG[cell.colour], color: COLOUR_TO_FG[cell.colour] }}>
                                            <span className="font-bold text-[15px]">{cell.day}</span>
                                            {cell.incidents.length > 0 && <span className="text-[10px] opacity-90">{cell.incidents.length} event{cell.incidents.length > 1 ? "s" : ""}</span>}
                                        </button>
                                    );
                                })}
                            </div>
                            <Legend />
                        </>)}
                    </div>
                    <div className="rounded-2xl bg-white border border-[#E5E0DA] p-5 space-y-4">
                        {data && (<>
                            <Totals totals={data.totals} byCategory={data.by_category} streak={data.streak_days} />
                            {selected && (
                                <div className="border-t border-[#F0EDE8] pt-3" data-testid="safety-cross-day-detail">
                                    <h4 className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64] mb-2">
                                        Day {selected.day} · {selected.incidents.length} event{selected.incidents.length === 1 ? "" : "s"}
                                    </h4>
                                    {selected.incidents.length === 0 ? (
                                        <div className="text-[12px] text-[#3F7D3A]">No events recorded.</div>
                                    ) : selected.incidents.map((i) => (
                                        <DayIncidentRow key={i.id} i={i} onClick={() => openIncidentDialog(i.id)} />
                                    ))}
                                    {viewerCanEdit && clientId && selected.colour !== "grey" && (
                                        <button data-testid="safety-cross-report-incident"
                                            onClick={() => setNewIncident({ client_id: clientId, building_id: buildingId || "", occurred_at: `${selected.date}T09:00` })}
                                            className="w-full mt-2 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-[12px] font-semibold text-white hover:opacity-90" style={{ background: "var(--brand-accent, #C75A1B)" }}>
                                            <Plus size={14} /> Report an incident on this day
                                        </button>
                                    )}
                                </div>
                            )}
                        </>)}
                    </div>
                </div>
            ) : (
                <AnnualGrid annual={annual} onDayClick={(date, incidents) => incidents.length && setDayModal({ date, incidents })} />
            )}

            {dayModal && (
                <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setDayModal(null)}>
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()} data-testid="annual-day-modal">
                        <h4 className="text-[13px] font-bold text-[#1F1F1F] mb-3">{dayModal.date} · {dayModal.incidents.length} event{dayModal.incidents.length > 1 ? "s" : ""}</h4>
                        {dayModal.incidents.map((i) => (
                            <DayIncidentRow key={i.id} i={i} onClick={() => { setDayModal(null); openIncidentDialog(i.id); }} />
                        ))}
                    </div>
                </div>
            )}

            {newIncident && (
                <IncidentDialog incident={null} prefill={newIncident} meta={meta} clients={clients} buildings={buildings}
                    portalClientId={!isInternalView ? portalClientId : null} viewerCanEdit={viewerCanEdit}
                    onClose={() => setNewIncident(null)} onSaved={async () => { setNewIncident(null); await refresh(); }} />
            )}
            {openIncident && (
                <IncidentDialog incident={openIncident} meta={meta} clients={clients} buildings={buildings}
                    portalClientId={!isInternalView ? portalClientId : null} viewerCanEdit={viewerCanEdit}
                    onClose={() => setOpenIncident(null)}
                    onSaved={async () => { await refresh(); try { const { data } = await api.get(`/incidents/${openIncident.id}`); setOpenIncident(data); } catch { setOpenIncident(null); } }} />
            )}
        </div>
    );
}

function Legend() {
    return (
        <div className="mt-4 flex items-center gap-3 flex-wrap text-[11px]">
            {Object.entries(COLOUR_LABEL).map(([c, l]) => (
                <div key={c} className="flex items-center gap-1.5">
                    <span className="inline-block w-3 h-3 rounded" style={{ background: COLOUR_TO_BG[c] }} />
                    <span className="text-[#6F6A64]">{l}</span>
                </div>
            ))}
        </div>
    );
}

function Totals({ totals, byCategory, streak }) {
    return (<>
        <div>
            <h4 className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64]">Month totals</h4>
            <div className="mt-2 space-y-1.5 text-[13px] text-[#1F1F1F]">
                <div className="flex justify-between"><span>Total events</span><span className="font-semibold" data-testid="safety-cross-total">{totals.total}</span></div>
                <div className="flex justify-between"><span>Minor</span><span>{totals.minor}</span></div>
                <div className="flex justify-between"><span>Moderate</span><span>{totals.moderate}</span></div>
                <div className="flex justify-between"><span>Major</span><span className="text-red-600 font-semibold">{totals.major}</span></div>
                <div className="flex justify-between"><span><ShieldAlert size={11} className="inline mr-1" />RIDDOR</span><span className="text-red-600 font-semibold">{totals.riddor}</span></div>
            </div>
        </div>
        {byCategory && (
            <div className="border-t border-[#F0EDE8] pt-3">
                <h4 className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64] mb-2">By category</h4>
                <div className="space-y-1 text-[12.5px] text-[#1F1F1F]">
                    {Object.entries(byCategory).filter(([, v]) => v > 0).map(([k, v]) => (
                        <div key={k} className="flex justify-between"><span>{k}</span><span className="font-semibold">{v}</span></div>
                    ))}
                    {Object.values(byCategory).every((v) => !v) && <div className="text-[#9A968F] italic">No events this month.</div>}
                </div>
            </div>
        )}
        <div className="border-t border-[#F0EDE8] pt-3">
            <h4 className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64]">Current green streak</h4>
            <div className="mt-1 text-2xl font-bold" data-testid="safety-cross-streak" style={{ color: "#3F7D3A" }}>{streak}</div>
            <div className="text-[11px] text-[#9A968F]">consecutive days without an event</div>
        </div>
    </>);
}

function DayIncidentRow({ i, onClick }) {
    return (
        <button data-testid={`safety-cross-incident-${i.id}`} onClick={onClick}
            className="w-full text-left bg-[#F5F3F0] hover:bg-[#F0EDE8] rounded p-2 mb-1.5 text-[12px] transition border border-transparent hover:border-[var(--brand-accent,_#C75A1B)]">
            <div className="flex items-center justify-between">
                <div className="font-mono text-[11px] text-[#9A968F]">{i.reference}</div>
                {i.riddor_reportable && <ShieldAlert size={11} className="text-red-600" />}
            </div>
            <div className="font-semibold text-[#1F1F1F]">{i.incident_type} · {i.severity}</div>
            <div className="text-[#6F6A64]">{i.status} · click to view details</div>
        </button>
    );
}

function AnnualGrid({ annual, onDayClick }) {
    if (!annual) return <div className="text-center text-[#9A968F] py-10">Select a client.</div>;
    return (
        <div data-testid="safety-cross-annual" className="space-y-4">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {annual.months.map((mo) => (
                    <div key={mo.month} className="rounded-xl bg-white border border-[#E5E0DA] p-3">
                        <div className="flex items-center justify-between mb-2">
                            <h4 className="text-[12px] font-bold text-[#1F1F1F]">{mo.month_name}</h4>
                            <span className="text-[11px] text-[#9A968F]">{mo.totals.total} event{mo.totals.total === 1 ? "" : "s"}</span>
                        </div>
                        <div className="grid grid-cols-7 gap-[3px]">
                            {[...Array(mo.first_weekday).fill(null), ...mo.cells].map((cell, idx) => {
                                if (!cell) return <div key={`p-${idx}`} />;
                                return (
                                    <button key={cell.day} onClick={() => onDayClick(cell.date, cell.incidents)}
                                        title={`${cell.date}${cell.incidents.length ? ` · ${cell.incidents.length} event(s)` : ""}`}
                                        className="aspect-square rounded-[3px] text-[8px] font-semibold flex items-center justify-center"
                                        style={{ background: COLOUR_TO_BG[cell.colour], color: COLOUR_TO_FG[cell.colour] }}>
                                        {cell.day}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
            <div className="rounded-2xl bg-white border border-[#E5E0DA] p-5 grid sm:grid-cols-3 gap-5">
                <div>
                    <h4 className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64] mb-2">Annual totals</h4>
                    <div className="space-y-1 text-[13px]">
                        <div className="flex justify-between"><span>Total events</span><span className="font-semibold">{annual.annual_totals.total}</span></div>
                        <div className="flex justify-between"><span>Major</span><span className="text-red-600 font-semibold">{annual.annual_totals.major}</span></div>
                        <div className="flex justify-between"><span><ShieldAlert size={11} className="inline mr-1" />RIDDOR</span><span className="text-red-600 font-semibold">{annual.annual_totals.riddor}</span></div>
                    </div>
                </div>
                <div>
                    <h4 className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64] mb-2">By category</h4>
                    <div className="space-y-1 text-[12.5px]">
                        {Object.entries(annual.annual_categories).filter(([, v]) => v > 0).map(([k, v]) => (
                            <div key={k} className="flex justify-between"><span>{k}</span><span className="font-semibold">{v}</span></div>
                        ))}
                    </div>
                </div>
                <div>
                    <h4 className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64] mb-2">Current green streak</h4>
                    <div className="text-2xl font-bold" style={{ color: "#3F7D3A" }}>{annual.streak_days}</div>
                    <div className="text-[11px] text-[#9A968F]">consecutive days without an event</div>
                    <div className="mt-3"><Legend /></div>
                </div>
            </div>
        </div>
    );
}
